/**
 * MarketplacePurchaseModal - Modal for purchasing scripts
 * 
 * Handles both free and paid script acquisitions.
 * For paid scripts, redirects to Stripe Checkout.
 */

import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseInit';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../shared/Modal';
import Button from '../shared/Button';
import LoadingSpinner from '../shared/LoadingSpinner';
import { getCategoryById } from './MarketplaceCategories';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import { useData } from '../../contexts/DataContext';

const MarketplacePurchaseModal = ({ listing, onClose, onSuccess }) => {
  const { user, userEmail, userTeam, effectiveTeamId } = useAuth();
  const { categories, loadMyPurchases } = useMarketplace();
  const { refreshTemplates } = useData();
  
  // Use effectiveTeamId for admins viewing other teams, otherwise use userTeam
  const targetTeamId = effectiveTeamId || userTeam;
  
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('confirm'); // 'confirm' | 'processing' | 'success'

  const category = getCategoryById(listing.category, categories);

  const handleFreePurchase = async () => {
    if (!targetTeamId) {
      setError('No team selected. Please ensure you are part of a team.');
      return;
    }
    
    setProcessing(true);
    setError(null);
    setStep('processing');

    try {
      // Use the dedicated free-script function (no Stripe dependency).
      // It creates the purchase record AND copies the template atomically.
      const acquireScript = httpsCallable(functions, 'purchaseFreeScript');
      const result = await acquireScript({
        listingId: listing.id,
        buyerTeamId: targetTeamId,
      });

      if (!result.data?.success) {
        throw new Error('Failed to acquire script. Please try again.');
      }

      // Refresh purchases and templates so the new script appears in My Scripts
      await Promise.all([
        loadMyPurchases(),
        refreshTemplates(),
      ]);
      
      setStep('success');
      setTimeout(() => {
        onSuccess?.();
      }, 2000);
    } catch (err) {
      console.error('Error processing free purchase:', err);
      // Map backend error codes to user-friendly messages
      const message = err.code === 'already-exists' 
        ? 'You have already acquired this script.'
        : err.message || 'Failed to process purchase. Please try again.';
      setError(message);
      setStep('confirm');
    } finally {
      setProcessing(false);
    }
  };

  const handlePaidPurchase = async () => {
    setProcessing(true);
    setError(null);
    setStep('processing');

    try {
      // Call Cloud Function to create Stripe Checkout session
      const createPurchase = httpsCallable(functions, 'createMarketplacePurchase');
      const result = await createPurchase({
        listingId: listing.id,
        buyerTeamId: targetTeamId,
        returnUrl: window.location.href
      });

      if (result.data?.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = result.data.checkoutUrl;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(err.message || 'Failed to initiate payment. Please try again.');
      setStep('confirm');
      setProcessing(false);
    }
  };

  const handlePurchase = () => {
    if (listing.isFree) {
      handleFreePurchase();
    } else {
      handlePaidPurchase();
    }
  };

  // Success state
  if (step === 'success') {
    return (
      <Modal isOpen onClose={onClose} title="Purchase Complete">
        <div className="text-center py-4">
          <div className="text-5xl mb-4">🎉</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Script Added to Your Team!
          </h3>
          <p className="text-gray-600 mb-4">
            "{listing.title}" is now available in your templates.
          </p>
          <p className="text-sm text-gray-500">
            You can find it in your Scripts list.
          </p>
        </div>
      </Modal>
    );
  }

  // Processing state
  if (step === 'processing') {
    return (
      <Modal isOpen onClose={() => {}} title="Processing">
        <div className="py-4 flex flex-col items-center">
          <LoadingSpinner size="large" message="Processing your purchase..." />
        </div>
      </Modal>
    );
  }

  // Confirmation state
  return (
    <Modal isOpen onClose={onClose} title={listing.isFree ? 'Get Free Script' : 'Purchase Script'}>
      <div className="space-y-4">
        {/* Script summary */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-2xl">
              {category.icon}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-900 truncate" title={listing.title}>{listing.title}</h4>
              <p className="text-sm text-gray-500">{category.name}</p>
              <p className="text-xs text-gray-400 mt-1">Created by {listing.sellerDisplayName}</p>
            </div>
            <div className="text-right">
              {listing.isFree ? (
                <span className="text-xl font-bold text-green-600">FREE</span>
              ) : (
                <span className="text-xl font-bold text-gray-900">${listing.price}</span>
              )}
            </div>
          </div>
        </div>

        {/* What you get */}
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-2">What you'll get:</h5>
          <ul className="text-sm text-gray-600 space-y-1">
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              Full script with {listing.nodeCount} conversation nodes
            </li>
            {listing.hasCoachingTips && (
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Coaching tips included
              </li>
            )}
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              Copy added to your team's templates
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              Edit and customize as needed
            </li>
          </ul>
        </div>

        {/* Terms reminder */}
        <div className="text-xs text-gray-500 p-3 bg-gray-50 rounded">
          By purchasing, you agree to our marketplace terms. Scripts are for your team's use only.
        </div>

        {/* Error message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handlePurchase}
            disabled={processing}
          >
            {processing ? 'Processing...' : listing.isFree ? 'Get Script' : `Pay $${listing.price}`}
          </Button>
        </div>

        {/* Payment info for paid scripts */}
        {!listing.isFree && (
          <div className="text-center text-xs text-gray-400">
            <span>🔒</span> Secure payment via Stripe
          </div>
        )}
      </div>
    </Modal>
  );
};

export default MarketplacePurchaseModal;
